import { computed, observable } from 'mobx';

import { Paginated } from '../../adl-gen/common';
import { DbKey, WithDbId } from '../../adl-gen/common/db';
import { TableQuery } from '../../adl-gen/common/tabular';
import { Skill } from '../../adl-gen/totalinteraction/futurefit/db';
import { Entry, RepositoryEntries, RepositoryEntrySet, SkillData } from '../../adl-gen/totalinteraction/futurefit/frontend';
import {
  makeRepositoryPutReq,
  makeRepositoryReq,
  QuerySkillDataReq,
  RepositoryPutEntry,
  RepositoryPutReq,
  RepositoryReq
} from '../../adl-gen/totalinteraction/futurefit/requests';
import { fromPromise, MaybeLoading } from '../../loader/loader';
import { Service } from '../../service/service';
import { LoggedIn, LoggedInSuper } from '../../stores/identity-store';

import { DataStore, SkillTrainingOfferingChange, SkillTrainingOfferingHelper, TransportLayer } from './index';

export interface SkillListAndData {
  // As this is deriving from the base class, it can be undefined. This needs proper sorting out
  selected?: WithDbId<Skill>;
  list?: Paginated<WithDbId<Skill>>;
  data: SkillData;
  repository: RepositoryEntrySet;
}

class SkillApi implements TransportLayer<Skill> {
  kind: string = 'Skill';
  constructor(protected readonly service: Service) {}
  create = async (item: Skill): Promise<DbKey<Skill>> => this.service.createSkill(item);
  update = async (itemWithId: WithDbId<Skill>): Promise<void> => this.service.updateSkill(itemWithId);
  doDelete = async (id: DbKey<Skill>): Promise<void> => this.service.deleteSkill(id);
  query = async (q: TableQuery): Promise<Paginated<WithDbId<Skill>>> => this.service.querySkills(q);

  uploadRepositoryEntry = async (req: RepositoryPutReq): Promise<Entry> => this.service.uploadRepositoryEntry(req);
  listRepositoryEntries = async (req: RepositoryReq): Promise<RepositoryEntries> =>
    this.service.listRepositoryEntries(req);

  querySkillData = async (req: QuerySkillDataReq): Promise<SkillData> =>
    this.service.querySkillData(req);
}

export class SkillStore extends DataStore<Skill> {
  @observable.ref repository: Promise<RepositoryEntrySet>;
  @observable skillData: Promise<SkillData>;

  skillTrainingOfferingHelper?: SkillTrainingOfferingHelper;

  static fromService = (
    service: Service, 
    identity: LoggedIn | LoggedInSuper, 
    id?: DbKey<Skill>
  ): SkillStore => {
    const store = new SkillStore(new SkillApi(service), identity, id, service);
    return store;
  };

  constructor(
    transport: TransportLayer<Skill>,
    identity: LoggedIn | LoggedInSuper,
    id?: DbKey<Skill>,
    service?: Service,
  ) {
    super(transport, identity, id);
    // TODO sort this out, as atm all skills are also loaded in skillData
    // -- Don't autoload all data, we might only want a subset - hence not in base class
    this.data = this.init(id);
    this.repository = this.initRepository();
    this.skillData = this.initData();
    if (service) this.skillTrainingOfferingHelper = SkillTrainingOfferingHelper.fromService(
      service,
      identity
    );
  }

  private readonly initData = async (): Promise<SkillData> => {
    return this.loadSkillData();
  }

  /// S3 Actions
  private readonly initRepository = async (): Promise<RepositoryEntrySet> => {
    const repository: RepositoryEntrySet = {};
    if (this.authorized) {
      return this.loadRepositories(repository);
    } else {
      return Promise.reject('Not authorized');
    }
  };

  private readonly loadRepositoryEntries = async (path: string): Promise<RepositoryEntrySet> => {
    return {
      [path]: await this.listRepositoryEntries(
        makeRepositoryReq({
          appClientId: this.identity.appClient.id,
          path
        })
      )
    };
  };

  private readonly loadRepositories = async (repository: RepositoryEntrySet): Promise<RepositoryEntrySet> => {
    try {
      const skillFolder: string = 'data-images/guidance/content/';
      const repo = await this.loadRepositoryEntries(skillFolder);
      return { ...repository, ...repo };
    } catch (e) {
      return Promise.reject(`Unable to load default repositories: ${(e as Error).message}`);
    }
  };

  private readonly listRepositoryEntries = async (req: RepositoryReq): Promise<RepositoryEntries> => {
    if (this.transport.kind === 'Skill') {
      return (this.transport as SkillApi).listRepositoryEntries(req);
    } else {
      return Promise.reject('Bad initialization of SkillStore');
    }
  };

  private readonly loadSkillData = async (): Promise<SkillData> => {
    if (this.authorized) {
      // For client admin we set the AppClient here but it's enforced on the server anyway
      // feature/recommendations-admin: In the current implementation the server will throw an error 
      // if a client admin attempts to load skills
      const req: QuerySkillDataReq = this.isSuperUser
        ? { kind: 'all', value: {} }
        : { kind: 'appClientId', value: this.identity.appClient.id };
      return (this.transport as SkillApi).querySkillData(req);
    } else {
      return Promise.reject('Not authorized');
    }
  }

  uploadRepositoryEntry = async (entry: RepositoryPutEntry): Promise<Entry> => {
    if (this.transport.kind === 'Skill') {
      return (this.transport as SkillApi).uploadRepositoryEntry(
        makeRepositoryPutReq({
          appClientId: this.identity.appClient.id,
          entry
        })
      );
    } else {
      return Promise.reject('Bad initialization of SkillStore');
    }
  };

  @computed
  get loadingRepository(): MaybeLoading<RepositoryEntrySet> {
    if (this.authorized) {
      return fromPromise(this.repository);
    } else {
      return { kind: 'error', error: 'Not authorized' };
    }
  }

  @computed
  get loadingData(): MaybeLoading<SkillData> {
    if (this.authorized) {
      return fromPromise(this.skillData);
    } else {
      return { kind: 'error', error: 'Not authorized' };
    }
  }

  loadAll = async () => {
    const list = await this.data;
    const data = await this.skillData;
    const repository = await this.repository;
    const selected = await this.selection;
    return { list, data, repository, selected };
  }

  @computed
  get loadingAll(): MaybeLoading<SkillListAndData> {
    if (this.authorized) {
      return fromPromise(this.loadAll());
    } else {
      return { kind: 'error', error: 'Not authorized' };
    }
  }

  public updateSkillTrainingOfferings = async (updates: SkillTrainingOfferingChange[]): Promise<void> => {
    if (this.skillTrainingOfferingHelper)
      await this.skillTrainingOfferingHelper.updateSkillTrainingOfferings(updates);
  };


}



// WEBPACK FOOTER //
// ./src/admin/stores/skill-store.ts